import { useState, type ReactNode } from 'react';
import { Loader2, Menu, MoreHorizontal } from 'lucide-react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../lib/utils';
import type { HeaderAction, PageHeaderConfig } from './types';

interface HeaderProps {
  head: PageHeaderConfig;
  onOpenMobileNav: () => void;
  /** Optional app-specific search input rendered between title and actions. */
  searchSlot?: ReactNode;
  /** Optional app-specific action buttons rendered before the theme toggle. */
  actions?: ReactNode;
  /** Shell-owned control that opens the right-side panel on phones. */
  panelToggle?: ReactNode;
  variant?: 'compact' | 'workspace';
}

export function Header({
  head,
  onOpenMobileNav,
  searchSlot,
  actions,
  panelToggle,
  variant = 'compact',
}: HeaderProps) {
  const configuredActions = isHeaderActionArray(head.actions)
    ? head.actions.filter((action) => !action.hidden)
    : null;
  const customActions = configuredActions ? undefined : (head.actions as ReactNode);
  const visibleActions = configuredActions?.filter((action) => !action.overflow) ?? [];
  const overflowActions = configuredActions?.filter((action) => action.overflow) ?? [];

  return (
    <header
      className={cn(
        // Only the top inset applies to a top bar; the bottom inset here used to
        // add dead space under the title on notched devices.
        'flex flex-shrink-0 items-center gap-2 border-b border-border bg-card px-3 pt-[max(env(safe-area-inset-top),0px)] sm:gap-3 sm:px-5',
        variant === 'workspace' ? 'min-h-[64px] py-2.5 sm:min-h-[76px] sm:py-3 lg:px-6' : 'min-h-[56px] sm:h-[54px]',
      )}
    >
      <Button
        variant="ghost"
        size="icon"
        className="h-9 w-9 md:hidden"
        onClick={onOpenMobileNav}
        aria-label="Open navigation"
      >
        <Menu className="h-5 w-5" />
      </Button>
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            'min-w-0 font-semibold leading-tight',
            variant === 'workspace' ? 'truncate text-xl tracking-tight' : 'truncate text-[15px]',
          )}
        >
          {head.title}
          {variant === 'compact' && head.subtitle && (
            <span className="ml-0 mt-0.5 block text-[11px] font-normal text-muted-foreground sm:ml-2 sm:mt-0 sm:inline">
              {head.subtitle}
            </span>
          )}
        </div>
        {variant === 'workspace' && head.subtitle && (
          <div className="mt-1 truncate text-xs font-normal text-muted-foreground sm:text-[13px]">
            {head.subtitle}
          </div>
        )}
        {/*
          Source/context chips are orientation for a desktop operator. On a
          phone they push the title and actions into a cramped three-line bar,
          so the page body carries that context instead.
        */}
        {variant === 'workspace' && head.context && (
          <div className="mt-1.5 hidden min-w-0 flex-wrap items-center gap-1.5 text-xs text-muted-foreground sm:flex">
            {head.context}
          </div>
        )}
      </div>

      {searchSlot}

      {configuredActions ? (
        <>
          <div className="hidden flex-shrink-0 items-center gap-2 sm:flex">
            {visibleActions.map((action) => (
              <HeaderActionControl key={action.id} action={action} />
            ))}
            {overflowActions.length > 0 && <HeaderActionOverflow actions={overflowActions} />}
          </div>
          {configuredActions.length > 0 && (
            <div className="flex-shrink-0 sm:hidden">
              <HeaderActionOverflow actions={configuredActions} label="Page actions" />
            </div>
          )}
        </>
      ) : (
        (customActions ?? actions)
      )}

      {panelToggle}
    </header>
  );
}

function isHeaderActionArray(value: PageHeaderConfig['actions']): value is HeaderAction[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as HeaderAction).id === 'string' &&
        typeof (item as HeaderAction).label === 'string',
    )
  );
}

function HeaderActionControl({ action }: { action: HeaderAction }) {
  const variant =
    action.variant === 'primary'
      ? 'default'
      : action.variant === 'destructive'
        ? 'destructive'
        : action.variant === 'ghost'
          ? 'ghost'
          : 'outline';
  const content = (
    <>
      {action.loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : action.icon}
      <span>{action.label}</span>
    </>
  );

  if (action.href) {
    return (
      <Button asChild size="sm" variant={variant} aria-disabled={action.disabled || action.loading}>
        <a
          href={action.href}
          onClick={(event) => {
            if (action.disabled || action.loading) event.preventDefault();
          }}
        >
          {content}
        </a>
      </Button>
    );
  }

  return (
    <Button
      type="button"
      size="sm"
      variant={variant}
      disabled={action.disabled || action.loading}
      onClick={action.onClick}
    >
      {content}
    </Button>
  );
}

function HeaderActionOverflow({
  actions,
  label = 'More actions',
}: {
  actions: HeaderAction[];
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" size="icon" variant="outline" className="h-8 w-8" aria-label={label}>
          <MoreHorizontal className="h-4 w-4" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-1.5">
        <div className="space-y-0.5">
          {actions.map((action) => {
            const className = cn(
              'flex min-h-9 w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              action.variant === 'destructive' && 'text-destructive hover:bg-destructive/10',
              (action.disabled || action.loading) && 'pointer-events-none opacity-50',
            );
            const content = (
              <>
                {action.loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  action.icon
                )}
                <span>{action.label}</span>
              </>
            );
            if (action.href) {
              return (
                <a
                  key={action.id}
                  href={action.href}
                  className={className}
                  aria-disabled={action.disabled || action.loading}
                  onClick={(event) => {
                    if (action.disabled || action.loading) event.preventDefault();
                    else setOpen(false);
                  }}
                >
                  {content}
                </a>
              );
            }
            return (
              <button
                key={action.id}
                type="button"
                className={className}
                disabled={action.disabled || action.loading}
                onClick={() => {
                  setOpen(false);
                  action.onClick?.();
                }}
              >
                {content}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
